import React from 'react';
import type { AgentStats } from '../../services/agentService';
import { TYPE } from '../../constants/typeScale';

/**
 * The derived four-axis "character sheet" for an agent — capability/intelligence, execution power,
 * personality, language skills. Monochrome dot-meter styling (branding: no rainbow). Presentational;
 * fed by getAgentBio().stats. Compact variant drops the header for embedding under an existing card.
 */
const DotMeter: React.FC<{ value: number }> = ({ value }) => (
  <span className="inline-flex items-center gap-1" aria-label={`${value} of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} className={`w-1.5 h-1.5 rounded-full ${i <= value ? 'bg-secondary-900 dark:bg-white' : 'bg-secondary-200 dark:bg-secondary-700'}`} />
    ))}
  </span>
);

const StatRow: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className={`${TYPE.control} text-secondary-500 dark:text-secondary-300`}>{label}</span>
    <DotMeter value={value} />
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className={`mb-2 ${TYPE.label} text-secondary-400`}>{title}</p>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className={`inline-flex items-center rounded-full border border-secondary-200 dark:border-secondary-600 px-2 py-0.5 ${TYPE.meta} text-secondary-600 dark:text-secondary-200`}>{children}</span>
);

const AgentStatCard: React.FC<{ stats: AgentStats }> = ({ stats }) => {
  const p = stats.personality;
  return (
    <div className="space-y-4">
      <Section title="Capability">
        <StatRow label="Intelligence" value={stats.intelligence} />
        <StatRow label="Knowledge" value={stats.knowledge} />
      </Section>

      <Section title="Execution power">
        <StatRow label="Execution" value={stats.execution} />
        {stats.powers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">{stats.powers.map((pw) => <Chip key={pw}>{pw}</Chip>)}</div>
        )}
      </Section>

      <Section title="Personality">
        <div className="flex flex-wrap gap-1.5 pb-1">
          <Chip>{p.archetype}</Chip>
          <Chip>{p.mbti}</Chip>
          <Chip>{p.humor} humor</Chip>
        </div>
        <StatRow label="Warmth" value={p.warmth} />
        <StatRow label="Energy" value={p.energy} />
        <StatRow label="Wit" value={p.wit} />
        <StatRow label="Creativity" value={p.creativity} />
      </Section>

      <Section title="Language skills">
        <div className="flex flex-wrap gap-1.5">
          {stats.languages.map((l) => <Chip key={l.name}>{l.name} · {l.fluency}</Chip>)}
        </div>
      </Section>
    </div>
  );
};

export default AgentStatCard;
